import { formatDate } from '@angular/common';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  Cell,
  Columns,
  ICreatePDF,
  IFonts,
  IStyleDefinition,
  ITable,
  PdfMakeWrapper,
  Table,
  Txt,
} from 'pdfmake-wrapper';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { AnnotationGroup } from '@mhe/reader/models';
import { robotoFont } from '../utils/roboto-font';

@Injectable({ providedIn: 'root' })
export class ExportNotesService {
  constructor(private readonly translateService: TranslateService) {}

  export(
    annotationsByChapter: AnnotationGroup[],
    bookTitle: string,
    fonts: IFonts = robotoFont,
  ): Observable<ICreatePDF> {
    PdfMakeWrapper.setFonts(fonts);

    return of(new PdfMakeWrapper()).pipe(
      tap((pdf) => this.setupPage(pdf)),
      tap((pdf) => this.setupFooter(pdf)),
      tap((pdf) => this.setupHeaders(pdf, bookTitle)),
      tap((pdf) => pdf.add(this.createNoteTable(annotationsByChapter))),
      map((pdf) => pdf.create()),
    );
  }

  private setupPage(pdf: PdfMakeWrapper): void {
    pdf.pageSize('LETTER');
    pdf.pageMargins([40, 80, 40, 60]);
    pdf.styles({
      header: {
        bold: true,
        fontSize: 18,
      },
      subheader: {
        bold: true,
        fontSize: 14,
      },
      footer: {
        fontSize: 10,
        italics: true,
      },
      selectedText: {
        bold: true,
      },
    });
  }

  private setupFooter(pdf: PdfMakeWrapper): void {
    pdf.footer((page, pages) => {
      const footer = new Columns([
        new Txt('').alignment('left').margin([20, 0, 0, 0]).end,
        new Txt([
          new Txt(page.toString()).italics().end,
          new Txt(this.translateService.instant('export.of')).end,
          new Txt(pages.toString()).italics().end,
        ]).alignment('center').end,
        new Txt(formatDate(new Date(), 'shortDate', 'en_US'))
          .alignment('right')
          .margin([0, 0, 20, 0]).end,
      ])
        .style('footer')
        .margin([20, 30, 20, 40]).end;
      return footer;
    });
  }

  private setupHeaders(pdf: PdfMakeWrapper, bookTitle: string): void {
    pdf.add(
      new Txt(this.translateService.instant('export.title_notes')).style(
        'subheader',
      ).end,
    );
    pdf.add(new Txt(bookTitle).style('header').margin([0, 0, 20, 20]).end);
  }

  private createNoteTable(annotationsByChapter: AnnotationGroup[]): ITable {
    console.log('Creating note table with annotations:', annotationsByChapter);
    const rows: IStyleDefinition[][] = annotationsByChapter.reduce(
      (abcAcc, chapter): IStyleDefinition[][] => [
        ...abcAcc,
        ...chapter.annotations
          .filter((a) => !!a.note)
          .reduce(
            (aAcc, annotation) => [
              ...aAcc,
              [
                new Cell({ text: chapter.groupLabel })
                  .background('lightgray')
                  .decoration('underline')
                  .decorationStyle('dashed')
                  .decorationColor('#C62828').end,
                new Txt('').end,
                new Txt(
                  formatDate(
                    annotation.updatedAt as string,
                    'shortDate',
                    'en-US',
                  ),
                )
                  .alignment('right')
                  .margin([20, 0, 0, 0]).end,
              ],
              [
                new Cell({ text: annotation.text })
                  .colSpan(3)
                  .margin([0, 0, 10, 0])
                  .style('selectedText').end,
              ],
              [
                new Cell({ text: this.breakText(annotation.note, 75) })
                  .colSpan(3)
                  .margin([0, 0, 10, 10]).end,
              ],
            ],
            [],
          ),
      ],
      [],
    );

    return new Table(rows).layout('noBorders').widths(['*', 0, 80]).end;
  }

  /**
   * Splits the input text into multiple lines, each with a maximum length of `maxLength`.
   * Tries to break lines at the last space within the segment to avoid breaking words.
   * If no space is found, breaks at the maximum length.
   *
   * @param text - The input string to break into lines.
   * @param maxLength - The maximum length of each line.
   * @returns The text split into lines, joined by newline characters.
   */
  private breakText(text: string | undefined, maxLength: number): string {
    if (!text) return '';
    const lines: string[] = [];
    let i = 0;
    while (i < text.length) {
      // Get the next segment of text up to maxLength
      const segment = text.slice(i, i + maxLength);
      if (!segment.includes(' ')) {
        // If there are no spaces, break at maxLength
        lines.push(segment);
      } else {
        // Try to break at the last space within the segment
        const lastSpaceIndex = segment.lastIndexOf(' ');
        if (lastSpaceIndex === -1) {
          // No space found, break at maxLength
          lines.push(segment);
        } else {
          // Break at the last space
          lines.push(segment.slice(0, lastSpaceIndex));
          i += lastSpaceIndex + 1; // Move past the space
          continue; // Skip the increment below
        }
      }
      i += maxLength;
    }
    return lines.join('\n');
  }
}
